import { useCallback, useMemo } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import ContainerTitle from '@/components/general/ContainerTitle';
import Loading from '@/components/general/Loading';
import PageTitle from '@/components/general/PageTitle';
import RotatingQuestionItem from '@/components/RotatingQuestionItem';
import { Question } from '@/types/api';
import { Paths } from '@/types/routes';
import styles from './RotatingQuestionsList.styles';

type RotatingQuestionsListPageProps = {
  activeQuestions: Question[];
  inactiveQuestions: Question[];
  isLoading: boolean;
  handleDeleteQuestion: (questionId: string | number) => void;
};

const RotatingQuestionsListPage = function ({
  activeQuestions,
  inactiveQuestions,
  handleDeleteQuestion,
  isLoading,
}: RotatingQuestionsListPageProps): JSX.Element {
  const renderQuestions = useCallback(
    (questions: Question[], type: 'active' | 'inactive') => {
      const emptyLabel =
        type === 'active'
          ? 'No queued rotating questions here yet'
          : 'No inactive rotating questions here yet';

      if (isLoading) {
        return <Loading size={25} />;
      }

      if (!questions.length) {
        return (
          <Typography variant="h6" color="primary.light" align="center">
            {emptyLabel}
          </Typography>
        );
      }

      return questions.map((question) => (
        <RotatingQuestionItem
          key={question.id}
          question={question}
          handleDeleteQuestion={handleDeleteQuestion}
        />
      ));
    },
    [isLoading, handleDeleteQuestion]
  );

  const renderActiveQuestions = useMemo(
    () => renderQuestions(activeQuestions, 'active'),
    [renderQuestions, activeQuestions]
  );

  const renderInactiveQuestions = useMemo(
    () => renderQuestions(inactiveQuestions, 'inactive'),
    [renderQuestions, inactiveQuestions]
  );

  return (
    <Box data-testid="rotating-questions-list-page">
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        justifyContent="space-between"
      >
        <PageTitle title="Rotating Questions" />

        <Button
          variant="outlined"
          sx={styles.addQuestionButton}
          component={Link}
          to={Paths.MANAGE_ROTATING_QUESTION}
        >
          Add rotating question
        </Button>
      </Box>

      <Paper variant="outlined" sx={styles.queueContainer} data-testid="queue-questions-container">
        <ContainerTitle title="Queued" />

        {renderActiveQuestions}
      </Paper>

      <Paper
        variant="outlined"
        sx={styles.queueContainer}
        data-testid="inactive-questions-container"
      >
        <ContainerTitle title="Inactive" />

        {renderInactiveQuestions}
      </Paper>
    </Box>
  );
};

export default RotatingQuestionsListPage;
